import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { clientsApi, getErrorMessage } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ColumnsMenu, useHiddenColumns } from '../components/common/ColumnsMenu';
import { DensityToggle, useDensity } from '../components/common/DensityToggle';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus,
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  Eye,
  Building2,
  Mail,
  Phone,
  MapPin,
  Loader2,
  Factory,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import ClientFormContent from '../components/ClientFormContent';

const CLIENTS_COLUMNS = [
  { key: 'company', label: 'Company', required: true },
  { key: 'industry', label: 'Industry' },
  { key: 'contact', label: 'Contact' },
  { key: 'added', label: 'Added' },
  { key: 'actions', label: 'Actions', required: true },
];

const Clients = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [hiddenClientsCols, setHiddenClientsCols] = useHiddenColumns('clients-table-cols');
  const [clientsDensity, setClientsDensity, clientsDensityClass] = useDensity('clients-table-density');
  const isCol = (key) => !hiddenClientsCols.has(key);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    notes: '',
  });
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false
  });

  // iter_43: debounced search term that's actually sent to the backend (so we
  // search across ALL clients, not just the current 20-row page).
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 200);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // iter_47: merged useEffect with filterSigRef — canonical pattern from
  // iter_45 (/candidates) and iter_46 (/jobs). Replaces the older two-effect
  // pattern that fired both a stale-page fetch AND the corrected page-1 fetch
  // when a user typed a search while on page > 1.
  const filterSigRef = React.useRef('');
  useEffect(() => {
    const sig = `${industryFilter}|${debouncedSearch}`;
    if (filterSigRef.current !== sig) {
      filterSigRef.current = sig;
      if (pagination.page !== 1) {
        setPagination((prev) => ({ ...prev, page: 1 }));
        return;
      }
    }
    fetchClients();
    fetchIndustries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, industryFilter, debouncedSearch]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        page_size: pagination.pageSize,
        ...(industryFilter !== 'all' && { industry: industryFilter }),
        ...(debouncedSearch && { search: debouncedSearch }),
      };
      const response = await clientsApi.getAll(params);
      const data = response.data;
      
      // Handle both paginated and legacy response formats
      if (data.items) {
        setClients(data.items);
        setPagination(prev => ({
          ...prev,
          total: data.total,
          totalPages: data.total_pages,
          hasNext: data.has_next,
          hasPrev: data.has_prev
        }));
      } else {
        // Legacy format (array)
        setClients(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchIndustries = async () => {
    try {
      const response = await clientsApi.getIndustries();
      setIndustries(response.data.industries || []);
    } catch (error) {
      console.error('Failed to load industries');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      industry: '',
      contact_person: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      notes: '',
    });
  };

  // Clean form data: convert empty strings to null for optional fields
  const cleanFormData = (data) => {
    const cleaned = { ...data };
    Object.keys(cleaned).forEach(key => {
      if (cleaned[key] === '') {
        cleaned[key] = null;
      }
    });
    return cleaned;
  };

  const handleCreate = async () => {
    setFormLoading(true);
    try {
      await clientsApi.create(cleanFormData(formData));
      toast.success('Client created successfully');
      setIsCreateOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create client'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdate = async () => {
    setFormLoading(true);
    try {
      await clientsApi.update(selectedClient.id, cleanFormData(formData));
      toast.success('Client updated successfully');
      setIsEditOpen(false);
      fetchClients();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update client'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (client) => {
    if (!window.confirm(`Are you sure you want to delete "${client.name}"?`)) return;

    try {
      await clientsApi.delete(client.id);
      toast.success('Client deleted successfully');
      fetchClients();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete client'));
    }
  };

  const openEdit = (client) => {
    setSelectedClient(client);
    setFormData({
      name: client.name || '',
      industry: client.industry || '',
      contact_person: client.contact_person || '',
      contact_email: client.contact_email || '',
      contact_phone: client.contact_phone || '',
      address: client.address || '',
      notes: client.notes || '',
    });
    setIsEditOpen(true);
  };

  const openView = (client) => {
    setSelectedClient(client);
    setIsViewOpen(true);
  };

  // Auto-open the client detail dialog when the URL carries `?view=<id>`
  // (used by global header search results so clicking a client drops the
  // user into the detail dialog instead of a filtered list).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const viewId = searchParams.get('view');
    if (!viewId) return;
    const next = new URLSearchParams(searchParams);
    next.delete('view');
    setSearchParams(next, { replace: true });

    let cancelled = false;
    (async () => {
      try {
        const res = await clientsApi.getById(viewId);
        if (!cancelled && res?.data) openView(res.data);
      } catch (err) {
        if (!cancelled) toast.error(getErrorMessage(err, 'Could not load client'));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search is now done server-side (iter_43); frontend just renders what
  // the API returned. Keep the variable name for readability.
  const filteredClients = clients;

  const canManageClients = ['admin', 'delivery_manager'].includes(user?.role);

  // Use extracted form component to prevent scroll issues
  const renderClientForm = (onSubmit, submitText) => (
    <ClientFormContent
      formData={formData}
      setFormData={setFormData}
      onSubmit={onSubmit}
      submitText={submitText}
      formLoading={formLoading}
      industries={industries}
    />
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Clients
          </h1>
          <p className="text-slate-600 mt-1">Manage your client companies</p>
        </div>
        {canManageClients && (
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-teal-700 hover:bg-teal-800" data-testid="add-client-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Client
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Client</DialogTitle>
                <DialogDescription>Enter client company details</DialogDescription>
              </DialogHeader>
              {renderClientForm(handleCreate, "Add Client")}
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search clients..."
                className="pl-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="client-search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Factory className="w-4 h-4 text-slate-400" />
              <Select
                value={industryFilter}
                onValueChange={setIndustryFilter}
              >
                <SelectTrigger className="w-[200px]" data-testid="industry-filter">
                  <SelectValue placeholder="Filter by Industry" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Industries</SelectItem>
                  {industries.map((industry) => (
                    <SelectItem key={industry} value={industry}>
                      {industry}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Clients Table */}
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-end gap-2 px-4 py-2 border-b border-slate-100">
            <DensityToggle density={clientsDensity} onChange={setClientsDensity} testid="clients-density" />
            <ColumnsMenu
              columns={CLIENTS_COLUMNS}
              hiddenKeys={hiddenClientsCols}
              onChange={setHiddenClientsCols}
              testid="clients-columns-menu"
            />
          </div>
          <Table
            containerClassName="max-h-[calc(100vh-280px)] min-h-[360px]"
            className={clientsDensityClass}
            data-testid="clients-table"
          >
            <TableHeader className="sticky top-0 z-10 bg-white shadow-sm">
              <TableRow>
                <TableHead>Company</TableHead>
                {isCol('industry') && <TableHead>Industry</TableHead>}
                {isCol('contact') && <TableHead>Contact</TableHead>}
                {isCol('added') && <TableHead>Added</TableHead>}
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredClients.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={
                      2 + ['industry', 'contact', 'added'].filter((k) => isCol(k)).length
                    }
                    className="text-center py-8 text-slate-500"
                  >
                    No clients found
                  </TableCell>
                </TableRow>
              ) : (
                filteredClients.map((client) => (
                  <TableRow key={client.id} className="table-row-hover">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-teal-700" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{client.name}</p>
                          {client.address && (
                            <p className="text-sm text-slate-500 flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {client.address}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    {isCol('industry') && <TableCell>{client.industry || '-'}</TableCell>}
                    {isCol('contact') && (
                      <TableCell>
                        <div className="space-y-1">
                          {client.contact_person && (
                            <p className="text-sm text-slate-900">{client.contact_person}</p>
                          )}
                          {client.contact_email && (
                            <p className="text-xs text-slate-500 flex items-center gap-1">
                              <Mail className="w-3 h-3" /> {client.contact_email}
                            </p>
                          )}
                          {client.contact_phone && (
                            <p className="text-xs text-slate-500 flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {client.contact_phone}
                            </p>
                          )}
                        </div>
                      </TableCell>
                    )}
                    {isCol('added') && (
                      <TableCell className="text-slate-500">
                        {format(new Date(client.created_at), 'MMM dd, yyyy')}
                      </TableCell>
                    )}
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`client-menu-${client.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openView(client)}>
                            <Eye className="w-4 h-4 mr-2" /> View
                          </DropdownMenuItem>
                          {canManageClients && (
                            <>
                              <DropdownMenuItem onClick={() => openEdit(client)}>
                                <Pencil className="w-4 h-4 mr-2" /> Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDelete(client)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div
          className="flex items-center justify-between px-2"
          data-testid="clients-pagination"
        >
          <p className="text-sm text-slate-500" data-testid="clients-pagination-summary">
            Showing {((pagination.page - 1) * pagination.pageSize) + 1} - {Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total} clients
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={!pagination.hasPrev}
              data-testid="clients-pagination-prev-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>
            <span className="text-sm text-slate-600" data-testid="clients-pagination-page-label">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={!pagination.hasNext}
              data-testid="clients-pagination-next-btn"
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Client</DialogTitle>
            <DialogDescription>Update client details</DialogDescription>
          </DialogHeader>
          {renderClientForm(handleUpdate, "Update Client")}
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedClient?.name}</DialogTitle>
            <DialogDescription>{selectedClient?.industry || 'Client Company'}</DialogDescription>
          </DialogHeader>
          {selectedClient && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {selectedClient.contact_person && (
                  <div>
                    <h4 className="font-medium text-slate-900 mb-1">Contact Person</h4>
                    <p className="text-slate-600">{selectedClient.contact_person}</p>
                  </div>
                )}
                {selectedClient.contact_email && (
                  <div>
                    <h4 className="font-medium text-slate-900 mb-1">Email</h4>
                    <p className="text-slate-600">{selectedClient.contact_email}</p>
                  </div>
                )}
                {selectedClient.contact_phone && (
                  <div>
                    <h4 className="font-medium text-slate-900 mb-1">Phone</h4>
                    <p className="text-slate-600">{selectedClient.contact_phone}</p>
                  </div>
                )}
                {selectedClient.address && (
                  <div>
                    <h4 className="font-medium text-slate-900 mb-1">Address</h4>
                    <p className="text-slate-600">{selectedClient.address}</p>
                  </div>
                )}
              </div>

              {selectedClient.notes && (
                <div>
                  <h4 className="font-medium text-slate-900 mb-1">Notes</h4>
                  <p className="text-slate-600">{selectedClient.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Clients;
